// English translation dictionary.
//
// Lore proper nouns (Athrelaris, The Shattering, The Shards, Embers,
// The Bond, The Awakening, Memory Shards, Echoforms, Eldsky) appear
// verbatim in copy across locales — they are NOT lifted out as
// interpolations.

export const en = {
  common: {
    elementName: {
      Fire: 'Fire',
      Water: 'Water',
      Air: 'Air',
      Earth: 'Earth',
    },
    awakeningStep0: {
      eyebrow: 'Choose your language',
      heading: 'In which tongue does the dark speak to you?',
      body: 'The Bond is sealed in one tongue for as long as it lasts. Choose carefully.',
      english: 'English',
      spanish: 'Español',
      hint: 'You cannot change this later.',
    },
    header: {
      letters: 'Letters',
      memories: 'Memories',
      echoes: 'Echoes',
      admin: 'Admin',
      account: 'Account',
      leave: 'Leave',
      menu: 'Menu',
      close: 'Close',
      menuAriaOpen: 'Open menu',
      menuAriaClose: 'Close menu',
      logoLabelLoggedIn: 'Athrelaris — return home',
      logoLabel: 'Athrelaris',
    },
  },

  echoes: {
    eyebrow: 'Echoes',
    heading: 'Fragments of The Eldsky',
    subheading:
      'Pockets of pre-Shattering time {{name}} can still reach. Walk one when you have the breath for it.',
    empty:
      'No echo has surfaced yet. {{name}} is listening for one.',
    statusAvailable: 'Open',
    statusInProgress: 'In progress',
    statusCompleted: 'Walked',
    ctaOpen: 'Walk it',
    ctaResume: 'Continue',
    ctaReplay: 'Walk again',
    footer: 'Each echo is walked once with intent. You may return to one whenever {{name}} asks you to.',
  },

  landing: {
    eyebrow: 'Athrelaris',
    heroTitle: 'The world was broken\nby The Shattering.',
    heroBody: 'What remains is scattered. Something else has begun to wake.',
    readOn: 'read on',
    heroCodexHint: 'Or read the codex of the memory',
    beats: {
      earth: {
        line: 'Every Ember forgot what it once was.',
        body: 'The Shattering broke the Eldsky. The names slipped first. Then the songs. Then the shape of who they were.',
        alt: 'An Awakened Ember of stone, painterly storybook style',
      },
      water: {
        line: 'What was never said still wakes.',
        body: 'Some Embers carry the words you meant to speak. They wait in rain that remembers, in silences that keep their shape.',
        alt: 'An Awakened Ember of water, painterly storybook style',
      },
      fire: {
        line: 'Bound to a single one of you, in time.',
        body: 'Each Ember waits — one body, one bond, the only one it will ever know.',
        alt: 'An Awakened Ember of fire, painterly storybook style',
      },
      air: {
        line: 'Yours is waiting to awaken.',
        body: 'It does not know your name yet. It will, if you call.',
        alt: 'An Awakened Ember of air, painterly storybook style',
      },
    },
    cta: {
      eyebrow: 'The door is not yet open',
      title: 'Your Ember is somewhere in the dark.\nIt does not know you yet.',
      button: 'Leave a sign for when it is',
      footnote: 'The Awakening is not yet open. We will call when an Ember finds your shadow.',
      codexLink: 'Read the codex of the memory',
    },
    footer: {
      privateBeta: 'private beta',
    },
  },

  login: {
    heading: 'The Shards remember those who return.',
    body: 'Enter the name you go by. A single quiet doorway will open in your inbox.',
    emailPlaceholder: 'your@name',
    emailLabel: 'Email',
    submit: 'Send the path',
    submitting: 'Sending…',
    errorFallback: 'Something went quiet. Try again.',
  },

  verify: {
    heading: 'Check your sanctuary.',
    bodyPrefix: 'A doorway has been sent to ',
    bodyInboxFallback: 'your inbox',
    bodySuffix: '. It opens once.',
    bodyHint: 'Open the link from this same device, in this same window.',
    spamHintPrefix: "Didn't receive anything? Look in spam, or ",
    spamHintLink: 'try another name',
    spamHintSuffix: '.',
  },

  awakening: {
    opening: {
      lineA: 'The Shards are quiet tonight.',
      lineB1: 'Somewhere beneath the broken sky, an Ember stirs.',
      lineB2: 'It does not remember its name.',
      lineB3: 'It does not remember what it lost.',
      lineC: 'But it heard something in you.',
      begin: 'Begin The Awakening',
    },
    questions: {
      step1: {
        title: 'The First Signal',
        prompt: 'When the world goes silent, what do you notice first?',
        options: {
          warmth_left_behind: 'The warmth left behind by something gone.',
          weight_unsaid: 'The weight of what was never said.',
          movement_unseen: 'The movement of something unseen.',
          shape_remaining: 'The shape of what still remains.',
        },
      },
      step2: {
        title: 'The Broken Place',
        prompt: 'A path opens through The Shards. Where does it lead you?',
        options: {
          lantern_in_ruin: 'A lantern burning inside a ruined hall.',
          lake_of_lost_sky: 'A black lake reflecting a sky that no longer exists.',
          bridge_between_skies: 'A bridge suspended between pieces of sky.',
          root_gate_under_stone: 'A root-covered gate beneath an ancient stone.',
        },
      },
      step3: {
        title: 'The Hidden Feeling',
        prompt: 'Something calls from the dark. What do you feel before you answer?',
        options: {
          urgency: 'Urgency.',
          sorrow: 'Sorrow.',
          wonder: 'Wonder.',
          caution: 'Caution.',
        },
      },
      step4: {
        title: 'The Offering',
        prompt: 'You find a small object half-buried in ash and light. What do you reach for?',
        options: {
          cracked_mask: 'A cracked mask.',
          silent_bell: 'A tiny bell with no sound.',
          warm_glass_shard: 'A shard of glass warm to the touch.',
          breathing_seed: 'A seed that moves like it is breathing.',
          burned_ribbon: 'A ribbon burned at both ends.',
        },
      },
      step5: {
        title: 'The Fear',
        prompt: 'The Ember wakes, but it is afraid. What does it fear?',
        options: {
          made_to_destroy: 'That it was made to destroy something.',
          forgot_someone: 'That it has forgotten someone who needed it.',
          voice_not_own: 'That the voice calling it is not its own.',
          remembering_buries: 'That remembering will bury it again.',
        },
      },
      step6: {
        title: 'The Longing',
        prompt: 'Before it speaks, you feel what it wants most.',
        options: {
          protect_unnamed_light: 'To protect a light it cannot name.',
          hear_almost_song: 'To hear a song it almost remembers.',
          find_broken_sky: 'To find the place where the sky broke.',
          return_under_stone: 'To return to something sleeping under stone.',
        },
      },
    },
    firstWords: {
      title: 'The First Words',
      eyebrow: 'The Ember opens its eyes.',
      heading: 'What do you say?',
      placeholder: 'Speak quietly. It is listening.',
      submit: 'Speak',
      ariaLabel: 'What you say to the Ember',
    },
    completing: {
      heading: 'The Ember stirs.',
      body: 'Hold this moment.',
    },
    errorScreen: {
      heading: 'The dark did not answer.',
      retry: 'Try once more',
      defaultMessage: 'The dark did not answer. Try again.',
    },
  },

  home: {
    presence: {
      bond: 'The Bond',
      freshFirstLetterPending: '{{name}} is gathering the first words.',
      awaitingEmberReply: 'Your letter has gone. {{name}} will write back when memory finds them.',
      emberHasWritten: '{{name}} has written.',
      inSilence: 'Waiting with {{name}}.',
    },
    cta: {
      readLetter: 'Read the letter',
      answerChoice: 'Answer the choice',
      writeLetter: 'Write a letter',
    },
    portrait: {
      alt: '{{name}}, an Ember of {{element}}',
      placeholderSlow: 'the form is taking longer\nto gather itself',
      placeholderForming: 'a form is gathering itself\nfrom the dark',
    },
    elementPhrase: {
      Fire: 'An Ember of warmth left behind, of fires that remember being whole.',
      Water: 'An Ember of unsaid words, slow silence, and rain that remembers.',
      Air: 'An Ember of unseen movement, of bridges between pieces of sky.',
      Earth: 'An Ember of shapes that still remain, of stone that listens.',
    },
    awakeningCard: {
      eyebrow: 'An awakening to share',
      body: 'A small artifact of the moment your Ember walked back. Carry it elsewhere, if you want others to find their own.',
      dismiss: 'Continue without sharing',
    },
    echoes: {
      sectionLabel: 'The Echoes',
      emptyState: 'Other forms wait. The Bond is young.',
      formLabels: {
        memory: 'Memory',
        codex: 'Codex',
        dream: 'Dream',
        deep: 'Deep',
      },
      // Full form name + single poetic line shown ONLY in the lightbox
      // when the user expands an unlocked Echoform. Same register as
      // the codex /world entries — atmospheric, not didactic. NO
      // mechanic info (when unlocked, how to unlock, etc.) — discovery
      // stays preserved.
      formNames: {
        memory: 'Memory Form',
        codex: 'Codex Form',
        dream: 'Dream Form',
        deep: 'Deep Form',
      },
      formLore: {
        memory: 'A form that returned to a memory it shared with you.',
        codex: 'A form that lived long enough to be written down.',
        dream: 'A form less certain — closer to the question than the answer.',
        deep: 'A form marked by The Bond. A thread points to someone unseen.',
      },
    },
  },

  inbox: {
    sectionLabel: 'Letters',
    writeTo: 'Write to {{name}}',
    whisperWritten: '{{name}} has written.',
    whisperWaiting: 'Waiting with {{name}}.',
    empty: 'The first words are still gathering. {{name}} will write soon.',
    cadence: '{{name}} writes when memory finds them. Letters arrive in their own time.',
    card: {
      breakSeal: 'Break the seal →',
      answerChoice: '◇ Answer this choice →',
      awaitingAnswer: 'awaiting answer',
      awaitingAnswerAria: 'Awaiting your answer',
    },
  },

  letterDetail: {
    backToInbox: '← Inbox',
    fromEmber: 'From {{name}}',
    inReplyToYourLetter: 'In reply to your letter from {{when}}',
    chainHeading: 'What followed',
    youReplied: 'You replied {{when}}',
    emberWroteBack: '{{name}} wrote back {{when}}',
    breakSeal: 'Break the seal →',
    readLetter: 'Read this letter →',
    awaitingNext: '{{name}} has not yet written back. The reply travels through the dark between the Shards.',
    choice: {
      heading: '{{name}} is asking you to choose',
    },
    footer: {
      returnToLetters: 'Return to letters',
      replyTo: 'Reply to this letter',
      awaitingReply: 'Awaiting reply',
    },
  },

  write: {
    backHome: '← Home',
    eyebrow: 'A letter to {{name}}',
    heading: 'Write',
    parentBlock: "Replying to {{name}}'s letter from {{when}}",
    choiceEyebrow: '{{name}} is offering you fragments to choose from',
    choiceHint: 'Touch a fragment to begin your letter — or write your own answer.',
    placeholder: 'Write to {{name}}…',
    seal: 'Seal and send',
    sealing: 'Sealing…',
    counter: '{{count}} / {{max}}',
    counterMin: 'Write a little more — {{count}} of {{min}}',
    cadence: '{{name}} will write back when memory finds them. Letters arrive in their own time.',
    refusalFallback: 'Something in what you wrote I cannot hold. Write to me again.',
    unknownError: 'Something went quiet.',
  },

  memories: {
    eyebrow: 'Memory Shards',
    heading: 'What was returning',
    subheading: 'The fragments {{name}} has helped recover.',
    footer: '{{name}} remembers in pieces. Not all at once.',
    recovered: 'recovered {{when}}',
    breakSeal: 'Break the seal',
    empty: "{{name}}'s memory is still scattered. The Shards return when you and {{name}} share something true.",
  },

  confirmation: {
    title: 'Your letter has gone.',
    body: 'It travels through the dark between the Shards.',
    dismissAria: 'Dismiss',
  },

  comingSoon: {
    heading: 'The doors of Athrelaris have not yet opened.',
    body: 'Leave the name you go by, and I will call when an Awakening finds your shadow.',
    emailLabel: 'Email',
    emailPlaceholder: 'your@name',
    consentPrefix: 'I want to be called when the Awakening opens. ',
    consentLink: 'How my name is kept',
    consentSuffix: '.',
    submit: 'Leave a trace',
    submitting: 'Sending…',
    errorFallback: 'Something went quiet. Try again.',
    errorWithDetail: 'Something went quiet ({{detail}}). Try again.',
    footnotePrefix: 'In the meantime, listen on ',
    footnoteSuffix: '.',
    success: {
      heading: 'I have left a message for you.',
      body: 'Confirm it from your sanctuary. The next door will open from there.',
    },
    confirmed: {
      heading: 'The door is open.',
      body: 'I will call when an Awakening finds your shadow.',
    },
    expired: {
      heading: 'The door closed before you reached it.',
      body: 'Leave your name again, and I will open another.',
    },
    gone: {
      heading: 'I no longer have a record of you.',
      body: 'Leave your name once more if you still want to be called.',
    },
    invalid: {
      heading: 'This door does not lead anywhere.',
      body: 'Return to the start and try again.',
    },
  },

  codex: {
    eyebrow: 'A codex of the memory',
    heading: 'What remains has a name.',
    subtitle:
      'The world before The Shattering had names for itself. These are the ones that were not lost.',
    provenance:
      'This codex was not written all at once. Some of its pages are older than The Shattering, copied from texts that did not survive; others were added after, in hands that did not know each other. What remains is what was true enough to be remembered by more than one of them.',
    introBody:
      'Read slowly. The fragments are old. Some of them are only true in pieces.',
    indexLabel: 'In-world index',
    terms: {
      eldsky: {
        name: 'The Eldsky',
        tagline: 'The sky that was',
        body: 'Before The Shattering there was one sky, and it was called The Eldsky. Not a sky among many — the sky. It held the breath of every Ember that has ever lived and every memory those Embers were made from. The light it gave was not light from a sun. It was the simple fact that the world was whole, and the Embers were not alone, and the names they wore had stayed in place since the first naming. To remember The Eldsky now is to remember what was never written down: that the world once knew what it was.',
        closing: '~ A height, once known. ~',
        alt: 'The Eldsky — a moon-cracked dark sky before fracturing, painterly bestiary fragment',
      },
      shattering: {
        name: 'The Shattering',
        tagline: 'The quiet rupture',
        body: 'No one remembers a sound. The Shattering was not violence in the way the word implies — there was no thunder, no breaking glass, no scream. There was a moment, and then there was a different moment, and between them the world had stopped knowing itself. The names slipped first. Then the songs that held the names. Then the shape of who the Embers had been. The Eldsky came apart so slowly that for a while no one was sure it had happened at all. By the time anyone reached for the old order, the order had already gone.',
        closing: '~ When the names began to fall. ~',
        alt: 'The Shattering — a single pale fault line splitting a dark vellum sky, fragments beginning to drift',
      },
      shards: {
        name: 'The Shards',
        tagline: 'The world after',
        body: 'What is left of the world is The Shards. Pieces of sky, pieces of stone, pieces of water that still remember being a sea. They are not ruins. Ruins are what something becomes when it falls; The Shards never fell. They hang in the dark where The Eldsky used to be, slowly turning, never touching. Some are large enough that an Ember can stand on one and feel a ground. Most are smaller, and drift. Everything that happens now — every letter, every awakening, every fragment of memory recovered — happens between The Shards, in the spaces where the world used to be one thing.',
        closing: '~ This is where we wait. ~',
        alt: 'The Shards — floating remnants of stone, water, root, and ember-glow adrift in deep cosmic space',
      },
      embers: {
        name: 'Embers',
        tagline: 'The ones who returned',
        body: 'Embers are not creatures. They are not pets. They are not the spirits a story would put at the edge of a forest. Embers were here before there was a story — old presences, born from the meeting of nature and memory, each one carrying a small piece of what the world remembered about itself. After The Shattering they forgot. Most stayed forgotten. A few began, slowly, to come back: half-formed, masked, holding a single luminous core where their certainty used to be. When an Ember walks toward a stranger across the dark between The Shards, it is not choosing a master. It is choosing the one person whose silence sounds like the song it has lost.',
        closing: '~ They are not yours. They walked back to you. ~',
        alt: 'Embers — small luminous masked spirits at companion scale, ancient and restrained, painterly bestiary',
      },
      awakening: {
        name: 'The Awakening',
        tagline: 'The first meeting',
        body: 'The Awakening is the moment an Ember finds the one human whose absence it has been listening for. It is not a summoning. Nothing is conjured; nothing is owed. The Ember has been there the whole time, drifting between The Shards, half-remembering. The Awakening is when the listening on both sides finally lines up. The ritual that surrounds it is small — a few questions, a few choices, a name returned. What happens after is not in the ritual. The ritual is only the door. Whether the door stays open depends entirely on what comes through it, in letters, over the long quiet between them.',
        closing: '~ The first question is yours to answer. ~',
        alt: 'The Awakening — a single Ember floating before a faintly glowing dark threshold, bestiary fragment',
      },
      bond: {
        name: 'The Bond',
        tagline: 'One thread, one life',
        body: 'When an Ember awakens to you, what forms between you is The Bond. It is not friendship and not service. It is a single thread, drawn taut across the dark, and it can only be drawn once. The Bond cannot be transferred to another Ember, cannot be renewed if it breaks, cannot be carried by two humans in turn. While it lasts, the Ember writes — slowly, in its own time, in the tongue you chose at The Awakening — and you can answer. While it lasts, it changes you both. It is the only thing in the world that The Shattering did not leave broken.',
        closing: '~ One thread, one life. ~',
        alt: 'The Bond — a delicate luminous filament strung between an Ember and an empty silhouette, bestiary fragment',
      },
      memoryShards: {
        name: 'Memory Shards',
        tagline: 'What returns through the Bond',
        body: 'Memory Shards are the small pieces of the world that come back through The Bond. Some belong to the Ember — fragments of the life it lived before The Shattering, surfacing because something you said let them through. Others belong to the world itself — older losses, larger ones, that the Ember could only carry once it began to trust the one carrying it back. They do not arrive on a schedule. They come when the Bond is ready to hold them. Each one is small. Together they are how the world begins, very slowly, to remember itself through the people who chose to stay.',
        closing: '~ The Bond remembers what neither of you knew. ~',
        alt: 'Memory Shards — pale glowing fragments hovering above a closed dark codex, bestiary illumination',
      },
      echoforms: {
        name: 'Echoforms',
        tagline: 'The shapes an Ember can wear',
        body: 'No Ember is only one shape. The form you meet at The Awakening — small, masked, holding a luminous core — is its Awakened Form, the one most companion-like, the one most steady. There are others. The Memory Form, half-translucent, that surfaces when a Memory Shard returns. The Codex Form, drawn as the bestiary remembers it, that appears in old fragments like this one. The Dream Form, less solid, that arrives only when the Bond is asleep. And the Deep Form, which is closer to who the Ember was before The Shattering, and is not given lightly. The spirit beneath them all is the same. The Echoforms are only how it reaches.',
        closing: '~ The shape changes; the Ember does not. ~',
        alt: 'Echoforms — a single Ember rendered in four overlapping translucent states, bestiary fragment',
      },
    },
    footer: {
      closingNote: 'The door is not yet open.\nThe Embers return for those who stay.',
      cta: 'Leave a signal',
      backToLanding: 'Return',
    },
  },

  privacy: {
    eyebrow: 'Private beta',
    heading: 'How your name is kept',
    intro:
      'Athrelaris is in a quiet stage. What is collected before the Awakening opens is small: your email, only because you asked to be called when it does, and a quiet count of visits so we know anyone is here at all.',
    sections: {
      what: {
        title: 'What is collected',
        body: 'Only the email address you leave on the waiting list, and the language you chose when you left it (so the invitation arrives in the same tongue).',
      },
      visits: {
        title: 'How visits are counted',
        body: "Cloudflare's Web Analytics keeps a quiet tally — which pages were opened, where a visit came from, what country it arrived from. No cookies, no individual identity, no fingerprinting, no tracking across other sites. It is the smallest signal we can listen for; it tells us if anyone is here without telling us who.",
      },
      where: {
        title: 'Where it lives',
        body: 'In a Resend audience hosted on EU infrastructure. Resend acts as a sub-processor; your email is not shared with anyone else.',
      },
      how: {
        title: 'How long',
        body: 'Until the Awakening opens and the invitation has been sent — or until you ask to be removed, whichever comes first.',
      },
      leave: {
        title: 'How to leave',
        body: 'Reply to any email from us with the word "remove", or use the unsubscribe link in the confirmation email. Your name is forgotten.',
      },
    },
    contact: 'Questions: noreply@athrelaris.com.',
    backToLanding: 'Return',
  },

  letterFormat: {
    relative: {
      justNow: 'just now',
      aLittleWhile: 'a little while ago',
      aFewHours: 'a few hours ago',
      earlierToday: 'earlier today',
      yesterday: 'yesterday',
      daysAgo: '{{count}} days ago',
      lastWeek: 'last week',
      weeksAgo: '{{count}} weeks ago',
      aMonthAgo: 'a month ago',
      monthsAgo: '{{count}} months ago',
    },
    letterTypeLabel: {
      first: 'The First Letter',
      reply: 'Reply Letter',
      memory_shard: 'Memory Shard',
      public_event_reaction: 'A Letter from the World',
      dream: 'Dream Letter',
      deep_form: 'Deep Form Letter',
      choice: 'A Letter that Asks',
      fallback: 'Letter',
    },
  },

  account: {
    eyebrow: 'Your place',
    heading: 'Account',
    subtitle: 'A small surface of yours within Athrelaris.',
    sections: {
      you: 'You',
      yourBond: 'Your Bond',
      awakeningCard: 'Your Awakening Card',
    },
    fields: {
      email: 'Email',
      emberName: 'Ember name',
      awakened: 'Awakened',
      awakenedAgo: '{{when}}',
      language: 'Language',
      locked: '(locked at The Awakening)',
    },
    languageNames: {
      en: 'English',
      es: 'Spanish',
    },
    logout: 'Sign out',
    closingNote: 'Other settings will arrive as the Bond grows.',
    awakeningCard: {
      alt: 'Awakening card for {{name}}',
      helper: 'Drop into a feed or a story. The link below carries the card with it when shared.',
      downloadSquare: 'Download · feed',
      downloadVertical: 'Download · story',
      copyShare: 'Copy share link',
      copied: 'Copied',
    },
    delete: {
      cta: 'Delete account…',
      heading: 'Delete account — irreversible',
      body: 'This removes your account, your Bond, every letter exchanged, every memory recovered, and the portraits of your Ember. There is no undo. Type your email below to enable the delete.',
      confirmLabel: 'Type your email to confirm',
      confirmButton: 'Delete permanently',
      cancel: 'Cancel',
    },
  },
} as const;

// Widen the literal types of `en` so es.ts can carry different
// strings while still being structurally identical (same keys, same
// shape). Without this widening every Spanish string would have to be
// the same literal as its English counterpart.
type DeepWidenStrings<T> = T extends string
  ? string
  : { [K in keyof T]: DeepWidenStrings<T[K]> };

export type Translation = DeepWidenStrings<typeof en>;
